import React, { useState, useEffect, useRef } from "react";
import { IonSpinner } from "@ionic/react";
import { Progress } from "antd";
import { isEmbroideryTrace } from "../../../config/appVariant";

const svgSteps = [
  { text: "Analyzing image colors...", progress: 12 },
  { text: "Mapping pixel boundaries...", progress: 28 },
  { text: "Building vector paths...", progress: 45 },
  { text: "Optimizing SVG curves...", progress: 62 },
  { text: "Simplifying geometry...", progress: 78 },
  { text: "Finalizing SVG output...", progress: 90 },
];

const embroiderySteps = [
  { text: "Analyzing image colors...", progress: 12 },
  { text: "Mapping pixel boundaries...", progress: 28 },
  { text: "Building embroidery paths...", progress: 45 },
  { text: "Optimizing vector curves...", progress: 62 },
  { text: "Simplifying geometry...", progress: 78 },
  { text: "Preparing for stitch conversion...", progress: 90 },
];

const conversionSteps = isEmbroideryTrace ? embroiderySteps : svgSteps;

const ConversionLoadingOverlay = ({ visible }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      timerRef.current = setTimeout(() => setShow(true), 150);
    } else {
      clearTimeout(timerRef.current);
      setShow(false);
      setStepIndex(0);
      setProgress(0);
    }
    return () => clearTimeout(timerRef.current);
  }, [visible]);

  useEffect(() => {
    if (!show) return;

    setStepIndex(0);
    setProgress(0);

    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const t = elapsed / 1000;

      // Fast-start, slow-finish curve:
      // ~40% in first 2s, ~70% by 5s, ~85% by 10s, asymptotes toward 95%
      const smoothProgress = 95 * (1 - Math.exp(-t / 3));
      setProgress(smoothProgress);

      let idx = 0;
      for (let i = conversionSteps.length - 1; i >= 0; i--) {
        if (smoothProgress >= conversionSteps[i].progress) {
          idx = i;
          break;
        }
      }
      setStepIndex(idx);
    }, 120);

    return () => clearInterval(interval);
  }, [show]);

  if (!show) return null;

  const step = conversionSteps[stepIndex];

  return (
    <div
      data-tutorial="conversion-loading"
      data-tutorial-busy="conversion-loading"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.25s ease",
      }}
    >
      <div
        style={{
          background: "white",
          padding: "40px 36px",
          borderRadius: "20px",
          width: "420px",
          maxWidth: "88vw",
          textAlign: "center",
          fontFamily: "Clash Grotesk",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <IonSpinner
            name="crescent"
            style={{ width: "36px", height: "36px", color: "#667eea" }}
          />
        </div>

        <h2
          style={{
            fontSize: "22px",
            fontWeight: 600,
            margin: "0 0 18px 0",
            color: "#1a202c",
          }}
        >
          {isEmbroideryTrace ? "Preparing Embroidery..." : "Converting to SVG..."}
        </h2>

        <p
          key={stepIndex}
          style={{
            fontSize: "14px",
            color: "#4a5568",
            fontWeight: 500,
            minHeight: "20px",
            marginBottom: "20px",
            transition: "opacity 0.3s ease",
          }}
        >
          {step.text}
        </p>

        <Progress
          percent={Math.round(progress)}
          strokeColor={{ "0%": "#667eea", "100%": "#764ba2" }}
          trailColor="#e2e8f0"
          strokeWidth={10}
          showInfo={false}
          style={{ marginBottom: "10px" }}
        />

        <div
          style={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#667eea",
          }}
        >
          {Math.round(progress)}%
        </div>

        <div
          style={{
            fontSize: "12px",
            color: "#a0aec0",
            marginTop: "16px",
          }}
        >
          {isEmbroideryTrace ? "Hang tight — this usually takes a few seconds..." : "This usually takes a few seconds..."}
        </div>
      </div>
    </div>
  );
};

export default ConversionLoadingOverlay;
